import {
  createFileRoute,
  type NavigateOptions,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { PageHeader } from "@/components/page-header";
import { PageShell } from "@/components/page-shell";
import { PlaidLinkForm } from "@/components/task-forms/plaid-link-form";
import {
  PLAID_LINK_RECONNECT_STORAGE_KEY,
  PLAID_LINK_TASK_STORAGE_KEY,
  PLAID_LINK_TOKEN_STORAGE_KEY,
  requireSession,
} from "@/lib/session";
import { useAdvanceFromTask } from "@/lib/task-progress";

function taskTarget(taskId: string | null | undefined): NavigateOptions {
  return taskId
    ? { to: "/steps/$taskId", params: { taskId } }
    : { to: "/steps" };
}

/**
 * Plaid's OAuth redirect URI (PlaidService#link_token). Banks that log in with
 * OAuth send the applicant back here with an `oauth_state_id`, and Link has to
 * be reinitialized with the same link token plus the received URI to finish.
 * The token and the task it started from (or, for a reconnect, the bank being
 * repaired) were stored by usePlaidLinkSession before leaving; without them
 * there is nothing to resume.
 */
export const Route = createFileRoute("/plaid-links")({
  component: PlaidOAuthReturnPage,
  beforeLoad: async ({ location }) => {
    await requireSession();

    const isOAuthReturn = new URLSearchParams(location.searchStr).has(
      "oauth_state_id",
    );
    if (!isOAuthReturn || !localStorage.getItem(PLAID_LINK_TOKEN_STORAGE_KEY)) {
      throw redirect(
        taskTarget(localStorage.getItem(PLAID_LINK_TASK_STORAGE_KEY)),
      );
    }
  },
});

function PlaidOAuthReturnPage() {
  const navigate = useNavigate();
  // Read once: the Link session clears them as soon as Link finishes.
  const [taskId] = useState(
    () => localStorage.getItem(PLAID_LINK_TASK_STORAGE_KEY) ?? undefined,
  );
  const [reconnectLinkId] = useState(
    () => localStorage.getItem(PLAID_LINK_RECONNECT_STORAGE_KEY) ?? undefined,
  );

  const onComplete = useAdvanceFromTask(taskId);

  // Back to the task page, where the applicant can start a fresh Link session.
  const onExit = useCallback(() => {
    void navigate(taskTarget(taskId));
  }, [navigate, taskId]);

  return (
    <PageShell>
      <PageHeader
        title={reconnectLinkId ? "Reconnect your bank" : "Connect your bank"}
      />
      <PlaidLinkForm
        taskId={taskId}
        reconnectLinkId={reconnectLinkId}
        onComplete={onComplete}
        onExit={onExit}
      />
    </PageShell>
  );
}
